import { create } from 'zustand';
import {
  Node,
  Edge,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
  Viewport, // Import Viewport
} from 'reactflow';
import { parseYaml, generateYaml } from '../utils/yamlUtils';
import { convertPlaybookToGraph, convertGraphToPlaybook } from '../utils/playbookConversion';
import { getLayoutedElements, LayoutOptions } from '../utils/layoutUtils'; // Import layout utility

// Define the structure for playbook-specific node data if needed
// For now, we can use a generic 'any' or define specific types later
export interface PlaybookNodeData { // Export the interface
  label: string;
  config?: any; // Placeholder for node-specific configuration from YAML
  mappings?: Mapping[]; // Add mappings type
  enrichments?: Enrichment[]; // Add enrichments type (placeholder)
  filters?: Filter[]; // Add filters type (placeholder)
  [key: string]: any; // Allow other properties
}

// Define specific types used within PlaybookNodeData
export interface Mapping { // Export the interface
  source: string;
  target: string;
}

// Placeholder types for Enrichment and Filter (can be refined later)
export interface Enrichment { // Export the interface
  // Define enrichment properties here
  [key: string]: any;
}

export interface Filter { // Export the interface
  // Define filter properties here
  [key: string]: any;
}


// Define the structure for the store's state
export interface PlaybookState { // Export the interface
  currentPlaybookYaml: string | null; // Store the original YAML content
  currentPlaybookObject: any | null; // Store the parsed YAML object
  playbookNodes: Node<PlaybookNodeData>[];
  playbookEdges: Edge[];
  selectedNode: Node<PlaybookNodeData> | null; // Add selectedNode
  selectedEdge: string | null; // Add selectedEdge
  viewport?: Viewport; // Add viewport
  helperLineHorizontal?: number; // Add helperLineHorizontal
  helperLineVertical?: number; // Add helperLineVertical

  // Actions
  setNodes: (nodes: Node<PlaybookNodeData>[]) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  loadPlaybookFromYaml: (yamlContent: string) => void;
  savePlaybookToYaml: () => string;
  updateNodeData: (nodeId: string, data: Partial<PlaybookNodeData>) => void;
  addPlaybookNode: (node: Node<PlaybookNodeData>) => void;
  deleteNode: (nodeId: string) => void; // Add deleteNode
  deleteEdge: (edgeId: string) => void; // Add deleteEdge
  setSelectedNode: (node: Node<PlaybookNodeData> | null) => void; // Add setSelectedNode
  setSelectedEdge: (edgeId: string | null) => void; // Add setSelectedEdge
  setViewport: (viewport: Viewport) => void; // Add setViewport
  setHelperLines: (horizontal?: number, vertical?: number) => void; // Add setHelperLines
  addNodesAtomically: (nodesToAdd: Node<PlaybookNodeData>[]) => void; // For adding multiple nodes at once
}

export const usePlaybookStore = create<PlaybookState>((set, get) => ({
  currentPlaybookYaml: null,
  currentPlaybookObject: null,
  playbookNodes: [],
  playbookEdges: [],
  selectedNode: null, // Initialize selectedNode
  selectedEdge: null, // Initialize selectedEdge
  viewport: undefined, // Initialize viewport
  helperLineHorizontal: undefined, // Initialize helper lines
  helperLineVertical: undefined,

  // --- Basic Setters ---
  setNodes: (nodes) => set({ playbookNodes: nodes }),
  setEdges: (edges) => set({ playbookEdges: edges }),
  setSelectedNode: (node) => set({ selectedNode: node }),
  setSelectedEdge: (edgeId) => set({ selectedEdge: edgeId }),
  setViewport: (viewport) => set({ viewport }),
  setHelperLines: (horizontal, vertical) => set({ helperLineHorizontal: horizontal, helperLineVertical: vertical }),

  onNodesChange: (changes) => {
    set({
      playbookNodes: applyNodeChanges(changes, get().playbookNodes),
    });
    // Reset helper lines on node changes
    set({ helperLineHorizontal: undefined, helperLineVertical: undefined });
  },

  onEdgesChange: (changes) => {
    set({
      playbookEdges: applyEdgeChanges(changes, get().playbookEdges),
    });
  },

  // --- Load/Save ---
  loadPlaybookFromYaml: async (yamlContent) => { // Make the function async
    try {
      const playbookObject = parseYaml(yamlContent);
      if (!playbookObject) throw new Error("Parsed YAML is null or undefined.");

      // 1. Get initial nodes and edges from YAML structure
      const { nodes: initialNodes, edges } = convertPlaybookToGraph(playbookObject);

      console.log("Loading Playbook YAML...");
      console.log("Parsed Object:", playbookObject);
      console.log("Initial Graph:", { nodes: initialNodes, edges });

      // 2. Apply auto-layout
      const layoutOptions: LayoutOptions = {
        direction: 'TB', // Top-to-Bottom layout
        spacing: [100, 150], // Example spacing [nodesep, ranksep]
      };
      // Need to wait briefly for initial node dimensions if not already set
      // This is a common pattern when using layout libraries with React Flow
      // We'll apply layout after a short delay to allow React Flow to render and measure nodes.
      // A more robust solution might involve checking node dimensions directly.
      setTimeout(async () => {
        // Get potentially updated nodes from state in case of rapid changes
        const currentNodes = get().playbookNodes;
        const currentEdges = get().playbookEdges;

        // Ensure nodes have dimensions before layout (React Flow might need a render cycle)
        // Check if at least some nodes have width/height
        const nodesHaveDimensions = currentNodes.some(n => n.width && n.height);

        if (!nodesHaveDimensions) {
          console.warn("Nodes lack dimensions, layout might be inaccurate. Retrying layout soon.");
          // Optionally retry after another short delay if needed
          // For now, we proceed, but layout might be based on defaults
        }

        try {
            const layoutedNodes = await getLayoutedElements(currentNodes, currentEdges, layoutOptions);
            console.log("Layouted Nodes:", layoutedNodes);
            set({ playbookNodes: layoutedNodes }); // Update state with layouted nodes
            console.log("Playbook node positions updated with auto-layout.");

            // Optionally fit the view after layout
            // This requires access to the React Flow instance, typically done in the component
            // or via a dedicated action if the instance is stored/accessible.
        } catch (layoutError) {
            console.error("Error applying auto-layout:", layoutError);
            // Proceed with unlayouted nodes if layout fails
        }

      }, 100); // Delay in ms - adjust as needed


      // 3. Set initial state (with un-layouted nodes first)
      set({
        currentPlaybookYaml: yamlContent,
        currentPlaybookObject: playbookObject,
        playbookNodes: initialNodes, // Set initial nodes first
        playbookEdges: edges,
        selectedNode: null, // Reset selections
        selectedEdge: null,
        viewport: undefined, // Reset viewport
      });
      console.log("Playbook state initially updated (pre-layout).");

    } catch (error) {
      console.error("Error loading playbook YAML:", error);
      alert(`Error loading YAML: ${error instanceof Error ? error.message : String(error)}`);
      // Reset state on error?
      set({
          currentPlaybookYaml: null,
          currentPlaybookObject: null,
          playbookNodes: [],
          playbookEdges: [],
          selectedNode: null,
          selectedEdge: null,
      });
    }
  },

  savePlaybookToYaml: () => {
    try {
      const { playbookNodes, playbookEdges } = get();
      const playbookObject = convertGraphToPlaybook(playbookNodes, playbookEdges); // Use the conversion function
      const yamlContent = generateYaml(playbookObject); // Use the YAML generator

      console.log("Saving Playbook to YAML. Current graph:", { playbookNodes, playbookEdges }); // Debug log
      console.log("Converted Object:", playbookObject); // Debug log
      console.log("Generated YAML:", yamlContent); // Debug log

      set({ currentPlaybookObject: playbookObject, currentPlaybookYaml: yamlContent }); // Update stored object/YAML
      return yamlContent;
    } catch (error) {
      console.error("Error saving playbook to YAML:", error);
      // Optionally, set an error state or show a notification
      return `# Error generating YAML: ${error instanceof Error ? error.message : String(error)}`;
    }
  },

  updateNodeData: (nodeId, data) => {
    set((state) => ({
      playbookNodes: state.playbookNodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...data } }
          : node
      ),
    }));
    // Note: Saving to YAML requires an explicit call to savePlaybookToYaml
  },

  // --- Add/Delete ---
  addPlaybookNode: (node) => {
    set((state) => ({
      playbookNodes: [...state.playbookNodes, node],
    }));
    // Note: Saving to YAML requires an explicit call to savePlaybookToYaml
  },

  addNodesAtomically: (nodesToAdd: Node<PlaybookNodeData>[]) => {
    set((state) => ({
      playbookNodes: [...state.playbookNodes, ...nodesToAdd],
    }));
    // Note: Saving to YAML requires an explicit call to savePlaybookToYaml
  },

  deleteNode: (nodeId) => {
    const nodeToDelete = get().playbookNodes.find((node) => node.id === nodeId);
    if (!nodeToDelete) return;

    // Basic node deletion (doesn't handle groups like useStore yet)
    const remainingNodes = get().playbookNodes.filter((node) => node.id !== nodeId);
    const remainingEdges = get().playbookEdges.filter(
      (edge) => edge.source !== nodeId && edge.target !== nodeId
    );

    set({
      playbookNodes: remainingNodes,
      playbookEdges: remainingEdges,
    });

    // Clear selection if deleted node was selected
    if (get().selectedNode?.id === nodeId) {
      set({ selectedNode: null });
    }
    // Note: Saving to YAML requires an explicit call to savePlaybookToYaml
  },

  deleteEdge: (edgeId) => {
    set((state) => ({
      playbookEdges: state.playbookEdges.filter((edge) => edge.id !== edgeId),
    }));
    // Clear selection if deleted edge was selected
    if (get().selectedEdge === edgeId) {
      set({ selectedEdge: null });
    }
    // Note: Saving to YAML requires an explicit call to savePlaybookToYaml
  },
}));

// Example usage (typically in components):
// const { playbookNodes, loadPlaybookFromYaml } = usePlaybookStore();
